import React from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import {Group} from '@pb/kit-component-group';
import {Loading} from '@pb/kit-component-loading';
import {H2} from '@pb/kit-component-typography';

import {useCompanyDetail} from './useCompanyDetail';

import {
    backLink,
    companyDetailContainer,
    detailCard,
    detailField,
    detailGrid,
    detailHeader,
    fieldLabel,
    fieldValue,
    sectionTitle,
} from './company-detail.styles.module.styl';

const formatDateTime = (dateStr: string | null | undefined): string => {
    if (!dateStr) {
        return '—';
    }
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return dateStr;
    }
};

const CompanyDetailView: React.FC = () => {
    const {companyId} = useParams<{companyId: string}>();
    const navigate = useNavigate();
    const {company, loading, error} = useCompanyDetail(companyId);

    if (loading) {
        return (
            <Group style={{justifyContent: 'center', padding: 60}}>
                <Loading/>
            </Group>
        );
    }

    if (error) {
        return (
            <div className={companyDetailContainer}>
                <span className={backLink} onClick={() => navigate(-1)} role="button" tabIndex={0}>
                    ← Back to Search
                </span>
                <div style={{color: '#cc0000', padding: 20}}>
                    {error}
                </div>
            </div>
        );
    }

    if (!company) {
        return (
            <div className={companyDetailContainer}>
                <span className={backLink} onClick={() => navigate(-1)} role="button" tabIndex={0}>
                    ← Back to Search
                </span>
                <div style={{padding: 20, textAlign: 'center', color: '#666'}}>
                    Company not found.
                </div>
            </div>
        );
    }

    return (
        <div className={companyDetailContainer}>
            <span
                className={backLink}
                onClick={() => navigate('/company-search')}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && navigate('/company-search')}>
                ← Back to Search
            </span>

            <div className={detailCard}>
                <div className={detailHeader}>
                    <div>
                        <H2>{company.companyName}</H2>
                        <div style={{fontSize: 14, color: '#666', marginTop: 4}}>
                            ID: {company.companyId} | COY: {company.coyId || '—'}
                        </div>
                    </div>
                    <span style={{
                        padding: '4px 12px',
                        borderRadius: 12,
                        fontSize: 13,
                        fontWeight: 500,
                        background: company.companyStatus === 'Active' ? '#e6f7e6' : '#f0f0f0',
                        color: company.companyStatus === 'Active' ? '#2d8a2d' : '#666',
                    }}>
                        {company.companyStatus || 'Unknown'}
                    </span>
                </div>

                {/* Identification Section */}
                <div className={sectionTitle}>Identification</div>
                <div className={detailGrid}>
                    <div className={detailField}>
                        <span className={fieldLabel}>Company ID</span>
                        <span className={fieldValue}>{company.companyId}</span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>COY ID</span>
                        <span className={fieldValue}>{company.coyId || '—'}</span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>CIK</span>
                        <span className={fieldValue}>{company.cik || '—'}</span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>Legal Name</span>
                        <span className={fieldValue}>{company.legalName || '—'}</span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>Short Name</span>
                        <span className={fieldValue}>{company.shortName || '—'}</span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>Country ID</span>
                        <span className={fieldValue}>{company.countryId ?? '—'}</span>
                    </div>
                </div>

                {/* Contact & Address Section */}
                <div className={sectionTitle}>Contact & Address</div>
                <div className={detailGrid}>
                    <div className={detailField}>
                        <span className={fieldLabel}>Phone</span>
                        <span className={fieldValue}>{company.phone || '—'}</span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>Fax</span>
                        <span className={fieldValue}>{company.fax || '—'}</span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>Address</span>
                        <span className={fieldValue}>
                            {[company.addressLine1, company.addressLine2, company.addressLine3, company.addressLine4]
                                .filter(Boolean).join(', ') || '—'}
                        </span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>City</span>
                        <span className={fieldValue}>{company.city || '—'}</span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>State</span>
                        <span className={fieldValue}>{company.state || '—'}</span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>Postal Code</span>
                        <span className={fieldValue}>{company.postalCode || '—'}</span>
                    </div>
                </div>

                {/* Financial Section */}
                <div className={sectionTitle}>Financial Details</div>
                <div className={detailGrid}>
                    <div className={detailField}>
                        <span className={fieldLabel}>Fiscal Year End Date</span>
                        <span className={fieldValue}>{formatDateTime(company.fiscalYearEndDate)}</span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>Last Fiscal Year End</span>
                        <span className={fieldValue}>{formatDateTime(company.lastFiscalYearEnd)}</span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>Reporting Type</span>
                        <span className={fieldValue}>{company.reportingType ?? '—'}</span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>Process Priority</span>
                        <span className={fieldValue}>{company.processPriority ?? '—'}</span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>FF Priority</span>
                        <span className={fieldValue}>{company.ffPriority || '—'}</span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>Manual Company</span>
                        <span className={fieldValue}>{company.isManualCompany ? 'Yes' : 'No'}</span>
                    </div>
                </div>

                {/* Audit Section */}
                <div className={sectionTitle}>Audit Information</div>
                <div className={detailGrid}>
                    <div className={detailField}>
                        <span className={fieldLabel}>Created</span>
                        <span className={fieldValue}>
                            {formatDateTime(company.creationDate)}
                            {company.createdBy != null && ` (by user ${company.createdBy})`}
                        </span>
                    </div>
                    <div className={detailField}>
                        <span className={fieldLabel}>Last Modified</span>
                        <span className={fieldValue}>
                            {formatDateTime(company.modificationDate)}
                            {company.modifiedBy != null && ` (by user ${company.modifiedBy})`}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompanyDetailView;
